import React, { useState, useRef, useEffect } from 'react';
import 'prop-types';
import { connect, useSelector } from 'react-redux';
import { useWalletCalcConfig } from 'wallet-manager';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import clsx from 'clsx';
import { LoadingSpinner, Button, Text } from 'reaxl';
import { usePrevious } from '@atc/use-previous';
import { useFeatures } from '@atc/features';
import { getLowestPersonalizedPayment, calculateDownPayment, getPersonalizedPaymentParams, getPersonalizedPaymentDetails } from '@atc/wallet-payment-fns';
import PersonalPayment from '../PersonalPayment/PersonalPayment.js';
import { jsx, jsxs } from 'react/jsx-runtime';
const WalletPaymentDetails = /*#__PURE__*/React.memo(_ref => {
  let {
    listing,
    isPreorder,
    isCompare,
    listingId,
    listingPayment,
    onMyWalletClick,
    paymentsInfo = {},
    paymentOption,
    personalPaymentProps = {},
    showMyWalletModal,
    userZip,
    updateListingPayments,
    updateOnPaymentChange = false,
    uiContext = 'ribbon',
    ...rest
  } = _ref;
  const {
    wallet_payment_option: [enablePaymentOption]
  } = useFeatures(['wallet_payment_option']);
  const {
    loanTerm,
    creditType,
    tradeIn = {},
    leaseTerm,
    leaseMileageRange
  } = paymentsInfo;
  const {
    amountOwed = 0,
    value: tradeInValue = 0
  } = tradeIn;
  const listingPreorder = useSelector(state => _get(state, `preorder.${listingId}`)) || {};
  const listingCard = isPreorder ? listingPreorder : listing;
  const defaultPayment = getLowestPersonalizedPayment(listingPayment, creditType, loanTerm)?.payment;
  const [personalizedPayment, setPersonalizedPayment] = useState(defaultPayment || null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    disableTrade
  } = useWalletCalcConfig(paymentsInfo, listing);
  const isInitialFetch = useRef(true);
  const {
    fetcher: fetchPayments,
    paymentsEnabled = true
  } = personalPaymentProps;
  const salePrice = _get(listingCard, 'pricingDetail.salePrice', '');
  const msrp = _get(listingCard, 'pricingDetail.msrp', '');
  const price = salePrice || msrp;
  const totalEquity = calculateDownPayment(price, paymentsInfo) + (tradeInValue - amountOwed);
  const paymentServices = _get(listingCard, 'paymentServices', {});
  const {
    paymentCalculationUrl
  } = paymentServices;
  let listingType = (listingCard.type || listingCard.listingType || 'used').toLowerCase();
  if (listingType === 'certified') {
    listingType = 'used';
  }
  if (userZip === null) {
    userZip = _get(listingCard, 'zip', null);
  }
  const {
    custom: customRate
  } = paymentsInfo?.interestRate || {};
  const useCustomRate = typeof customRate === 'number';
  const prevShowModal = usePrevious(showMyWalletModal, false);
  const prevPaymentsInfo = usePrevious(paymentsInfo, {});
  const prevListingId = usePrevious(listingId, null);

  // Native Deal Calculator needs to refetch on payment info change
  const fetchOnPaymentInfoChange = updateOnPaymentChange && !_isEqual(prevPaymentsInfo, paymentsInfo);
  // MyWallet modal should only refetch on modal close
  const fetchOnWalletModalClose = !updateOnPaymentChange && prevShowModal && !showMyWalletModal;
  const showRates = uiContext === 'withRates' || uiContext === 'default';
  const shouldUpdatePayment = fetchOnPaymentInfoChange || fetchOnWalletModalClose || showRates;
  // don't fetch payment if totalEquity amount is greater than price or no sales price
  const zeroPayments = !price || totalEquity >= price;

  // Should only fetch if NOT using a custom rate
  const shouldFetchPayment = !useCustomRate && shouldUpdatePayment;
  useEffect(() => {
    const fetchPersonalizedPayment = async () => {
      if (!!fetchPayments && paymentsEnabled && paymentCalculationUrl && listingId && !useCustomRate) {
        setIsLoading(true);
        const downPaymentValue = calculateDownPayment(price, paymentsInfo);
        let leaseParams = {};
        if (enablePaymentOption) {
          leaseParams = {
            leaseTerm,
            milesPerYear: leaseMileageRange,
            leaseDownPayment: downPaymentValue
          };
        }
        const personalizedPaymentsInfo = {
          ...paymentsInfo,
          ...leaseParams,
          includeCash: true,
          tradeIn: disableTrade ? undefined : tradeIn
        };
        const personalizedPaymentParams = getPersonalizedPaymentParams({
          paymentsInfo: personalizedPaymentsInfo,
          userZip
        });
        const {
          details,
          error
        } = await getPersonalizedPaymentDetails({
          fetchPayments,
          paymentCalculationUrl,
          personalizedPaymentParams
        });
        if (error || details?.error) {
          updateListingPayments({
            [listingId]: null
          });
          setPersonalizedPayment(null);
        }
        if (details && details.rates && Object.keys(details.rates).length) {
          const {
            payment
          } = await getLowestPersonalizedPayment(details, creditType, loanTerm);
          if (updateListingPayments && payment) {
            setPersonalizedPayment(payment);
            updateListingPayments({
              [listingId]: {
                ...details
              }
            });
          }
        } else if (updateListingPayments) {
          updateListingPayments({
            [listingId]: null
          });
          setPersonalizedPayment(null);
        }
        isInitialFetch.current = false;
      }
      setIsLoading(false);
    };
    if (useCustomRate) {
      setIsLoading(false);
      setPersonalizedPayment(null);
    }
    if (prevListingId !== listingId) {
      isInitialFetch.current = true;
    }
    if ((shouldFetchPayment || isInitialFetch.current) && !zeroPayments) {
      fetchPersonalizedPayment();
    }
    if (zeroPayments) {
      setIsLoading(false);
      setPersonalizedPayment(0);
    }
  }, [listingId, paymentsInfo, userZip, useCustomRate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading && listingId) {
    return /*#__PURE__*/jsx(LoadingSpinner, {
      className: "margin-top-0 margin-right-0 text-right"
    });
  }
  const paymentContainerClasses = clsx({
    'text-right': !showRates && !isCompare
  });
  const paymentAmountClasses = clsx({
    'border-right-1': showRates,
    'text-link text-size-200': uiContext !== 'withRates' || uiContext === 'default'
  }, 'display-block');

  // pull out payment from leaseOffers if wallet_payment_option FF and paymentOption in wallet is 'lease'
  const leasePayment = listingPayment?.rates?.[creditType]?.leaseOffers?.[leaseTerm]?.[0]?.payment;
  const isLeasePaymentOptionSelected = enablePaymentOption && paymentOption === 'lease';
  const getPersonalizedPayment = () => {
    if (isLeasePaymentOptionSelected) {
      if (leasePayment) {
        return leasePayment;
      }
    }
    return personalizedPayment;
  };

  // NOTE: If lease payment option is enabled and selected, but there is no lease payment render this text
  if (isLeasePaymentOptionSelected && !leasePayment) {
    return listingType === 'new' ? /*#__PURE__*/jsxs("div", {
      className: "text-right text-size-200",
      "data-cmp": "lnk-pymt-dtls",
      children: ["Contact Dealer ", /*#__PURE__*/jsx("br", {}), "for Leasing Options"]
    }) : null;
  }
  return !isLoading && listingId && price ? /*#__PURE__*/jsxs(Button, {
    className: clsx(paymentAmountClasses, paymentContainerClasses),
    "data-cmp": "lnk-pymt-dtls",
    onClick: onMyWalletClick,
    bsStyle: "text",
    children: [uiContext === 'withRates' && /*#__PURE__*/jsx(Text, {
      color: "subdued-lighter",
      size: 200,
      componentClass: "div",
      children: "Est. Payment"
    }), /*#__PURE__*/jsx(PersonalPayment, {
      paymentsInfo: paymentsInfo,
      price: price,
      textColor: showRates ? 'gray-base' : 'blue-darker',
      vehicleCondition: listingType,
      uiContext: uiContext,
      personalizedPayment: getPersonalizedPayment(),
      shouldUpdatePayment: shouldUpdatePayment,
      ...rest
    }, `personalPayment-${listingId}`), (!showRates || uiContext === 'default') && /*#__PURE__*/jsx("span", {
      className: "display-block text-link text-size-200 padding-bottom-3",
      children: `See ${isLeasePaymentOptionSelected && !!leasePayment ? 'Lease' : 'Finance'} Details`
    })]
  }, "renderSeeDetailsModalTrigger") : null;
}, (prevProps, nextProps) => _isEqual(prevProps.paymentsInfo, nextProps.paymentsInfo) && _isEqual(prevProps.listingPayments, nextProps.listingPayments) && _isEqual(prevProps.listingPayment, nextProps.listingPayment) && _isEqual(prevProps.showMyWalletModal, nextProps.showMyWalletModal) && _isEqual(prevProps.listing.paymentServices?.paymentCalculationUrl, nextProps.listing.paymentServices?.paymentCalculationUrl));
function mapStateToProps(state, _ref2) {
  let {
    listingId
  } = _ref2;
  const {
    inventory = {},
    payments = {}
  } = state;
  const {
    listingPayments = {},
    myWallet = {},
    paymentOption,
    ...paymentsRest
  } = payments;
  const {
    displayModal: showMyWalletModal
  } = myWallet;
  const listing = inventory[listingId] || {};
  const listingPayment = listingPayments[listingId] || {};
  const userZip = _get(state, 'user.location.zip', null);

  // NOTE: These are causing potenially uneeded rerenders
  delete paymentsRest.interactions;
  delete paymentsRest.penciling;
  delete paymentsRest.listingLeasePayments;
  delete paymentsRest.listingCashPayments;
  delete paymentsRest.dealType;
  delete paymentsRest.icoTradeIn;
  return {
    listing,
    listingPayment,
    showMyWalletModal,
    paymentsInfo: paymentsRest,
    paymentOption,
    userZip
  };
}
var WalletPaymentDetails$1 = /* #__PURE__*/connect(mapStateToProps)(WalletPaymentDetails);
export { WalletPaymentDetails$1 as default };
